import { type MessageCountCondition as MessageCountConditionType } from "@redotech/redo-flows-common/rules-v3/schemas";
import {
  MESSAGE_COUNT_SENDER_TYPES_V3,
  type MessageCountSenderTypeV3,
} from "@redotech/redo-flows-common/rules-v3/types";
import { NumericQueryOperator } from "@redotech/redo-flows-common/rules/types";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

const canEdit = (
  editableFields: ReadonlySet<string> | undefined,
  field: string,
) => editableFields === undefined || editableFields.has(field);

const messageSenderDisplayNames = (): Record<
  MessageCountSenderTypeV3,
  string
> => ({ customer: t("Customer"), merchant: t("Merchant"), ai: t("AI") });

const messageSenderItems = (): RedoListItem<MessageCountSenderTypeV3>[] =>
  MESSAGE_COUNT_SENDER_TYPES_V3.map((senderType) => ({
    id: senderType,
    type: "text" as const,
    text: messageSenderDisplayNames()[senderType],
    value: senderType,
  }));

export const MessageCountCondition = memo(function MessageCountCondition({
  condition,
  onChange,
  editableFields,
}: {
  condition: MessageCountConditionType;
  onChange: (updated: MessageCountConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const valueEditable = canEdit(editableFields, "value");
  const minValueEditable = canEdit(editableFields, "minValue");
  const maxValueEditable = canEdit(editableFields, "maxValue");
  const senderTypesEditable = canEdit(editableFields, "senderTypes");
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const selectedSenderItems = useMemo(
    () =>
      (condition.senderTypes ?? []).map((senderType) => ({
        id: senderType,
        type: "text" as const,
        text: messageSenderDisplayNames()[senderType],
        value: senderType,
      })),
    [condition.senderTypes],
  );
  const senderDisplayItem = useMemo(() => {
    if (selectedSenderItems.length === 0) {
      return undefined;
    }
    if (selectedSenderItems.length === 1) {
      return selectedSenderItems[0];
    }
    return {
      id: "display",
      type: "text" as const,
      text: selectedSenderItems
        .flatMap((item) => ("text" in item ? [item.text] : []))
        .join(", "),
      value: MESSAGE_COUNT_SENDER_TYPES_V3[0],
    };
  }, [selectedSenderItems]);

  const senderTypeSelector = (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="secondary">
        {t("Count messages from")}
      </Text>
      <RedoSingleSelectDropdownInputButton
        disabled={!senderTypesEditable}
        isDropdownOpen={dropdownOpen}
        onClick={() => senderTypesEditable && setDropdownOpen(!dropdownOpen)}
        placeholder={t("All message senders")}
        ref={setDropdownButtonRef}
        selectedItem={senderDisplayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={messageSenderItems()}
        selectedItems={selectedSenderItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          if (!senderTypesEditable) {
            return;
          }
          onChange({
            ...condition,
            senderTypes: items.map((item) => item.value),
          });
        }}
        size="xs"
      />
    </Flex>
  );

  switch (condition.queryOperator) {
    case NumericQueryOperator.GREATER_THAN:
    case NumericQueryOperator.LESS_THAN:
    case NumericQueryOperator.EQUAL_TO:
      return (
        <Flex dir="column" gap="sm" w="full">
          <Flex align="center" gap="sm">
            <RedoTextInput
              inputTypeHint="number"
              min={0}
              placeholder="0"
              setValue={(value: string) => {
                if (!valueEditable) {
                  return;
                }
                const num = parseInt(value, 10);
                onChange({ ...condition, value: isNaN(num) ? 0 : num });
              }}
              size="sm"
              state={valueEditable ? "default" : "disabled"}
              value={condition.value.toString()}
            />
            <Text fontSize="sm" textColor="secondary">
              {t("messages")}
            </Text>
          </Flex>
          {senderTypeSelector}
        </Flex>
      );

    case NumericQueryOperator.BETWEEN:
      return (
        <Flex dir="column" gap="sm" w="full">
          <Flex align="center" gap="sm">
            <RedoTextInput
              inputTypeHint="number"
              min={0}
              placeholder="0"
              setValue={(value: string) => {
                if (!minValueEditable) {
                  return;
                }
                const num = parseInt(value, 10);
                onChange({ ...condition, minValue: isNaN(num) ? 0 : num });
              }}
              size="sm"
              state={minValueEditable ? "default" : "disabled"}
              value={condition.minValue.toString()}
            />
            <Text fontSize="sm" textColor="secondary">
              {t("and", { context: "conjunction between two range values" })}
            </Text>
            <RedoTextInput
              inputTypeHint="number"
              min={0}
              placeholder="0"
              setValue={(value: string) => {
                if (!maxValueEditable) {
                  return;
                }
                const num = parseInt(value, 10);
                onChange({ ...condition, maxValue: isNaN(num) ? 0 : num });
              }}
              size="sm"
              state={maxValueEditable ? "default" : "disabled"}
              value={condition.maxValue.toString()}
            />
            <Text fontSize="sm" textColor="secondary">
              {t("messages")}
            </Text>
          </Flex>
          {senderTypeSelector}
        </Flex>
      );

    default:
      return null;
  }
});
